import {
  Button,
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@medimo/ui";
import { Settings, Shield } from "lucide-react";
import { type KeyboardEvent, useEffect, useRef } from "react";
import { useTOTPSetup } from "../../hooks/use-totp-setup";
import {
  CompleteStepContent,
  SetupStepContent,
  VerifyStepContent,
} from "./components";

interface TOTPSetupFormProps {
  onSetupComplete?: () => void;
}

export const TOTPSetupForm = ({ onSetupComplete }: TOTPSetupFormProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    currentStep,
    setupData,
    verificationCode,
    isLoading,
    isVerifying,
    secretCopied,
    verificationError,
    email,
    isModalOpen,
    handleVerifyCode,
    handleCopySecret,
    handleVerificationCodeChange,
    resetState,
    handleInitializeSetup,
    handleModalClose,
    handleNextToVerify,
    handleAnimationEnd,
  } = useTOTPSetup({ onSetupComplete });

  // Cleanup on unmount to prevent concurrent modifications
  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  // Focus management for verification step in modal
  useEffect(() => {
    if (currentStep === "verify" && isModalOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentStep, isModalOpen]);

  const renderModalContent = () => {
    switch (currentStep) {
      case "verify":
        return (
          <VerifyStepContent
            verificationCode={verificationCode}
            verificationError={verificationError}
            isVerifying={isVerifying}
            inputRef={inputRef}
            onCodeChange={handleVerificationCodeChange}
            onBack={handleModalClose}
            onVerify={handleVerifyCode}
          />
        );
      case "complete":
        return <CompleteStepContent onSetupComplete={handleModalClose} />;
      default:
        return null;
    }
  };

  const renderCurrentStep = () => {
    if (currentStep) {
      return (
        <SetupStepContent
          setupData={setupData}
          isLoading={isLoading}
          secretCopied={secretCopied}
          email={email}
          disabled={currentStep && currentStep !== "setup"}
          onCopySecret={handleCopySecret}
          onNext={handleNextToVerify}
          onCancel={resetState}
        />
      );
    }

    // Default state - show the setup button
    return (
      <div className="flex">
        <Button
          className="flex items-center gap-2 px-4"
          variant="outline_green"
          onClick={handleInitializeSetup}
          size="sm"
        >
          <Settings className="h-4 w-4" />
          ワンタイムパスワードを設定
        </Button>
      </div>
    );
  };

  const handleEnterKey = () => {
    if (currentStep === "verify" && verificationCode.length === 6) {
      handleVerifyCode();
    } else if (currentStep === "complete") {
      handleModalClose();
    }
  };

  const handleEscapeKey = () => {
    if (currentStep === "verify") {
      handleModalClose();
    } else if (currentStep === "complete") {
      handleModalClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleEnterKey();
    } else if (e.key === "Escape") {
      handleEscapeKey();
    }
  };

  return (
    <>
      {renderCurrentStep()}
      <Dialog open={isModalOpen} onOpenChange={handleModalClose}>
        <DialogContent
          className="sm:max-w-md"
          onAnimationEnd={handleAnimationEnd}
          onKeyDown={handleKeyDown}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {currentStep === "verify" ? "認証コードの確認" : "設定完了"}
            </DialogTitle>
            <DialogCloseButton onClick={handleModalClose} tabIndex={-1} />
          </DialogHeader>
          {renderModalContent()}
        </DialogContent>
      </Dialog>
    </>
  );
};
